import { useEffect, useId, useMemo, useState } from 'react';
import { Avatar, Box, Button, Chip, Drawer, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Document, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { useFetchData } from '../../../hooks/useFetchData/useFetchData';

const urlAttendants = process.env.REACT_APP_API + '/blip/attendants';
const urlOpenTickets = process.env.REACT_APP_API + '/blip/open-tickets';
const urlSla = process.env.REACT_APP_API + '/blip/sla';

const parseDurationToSeconds = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length !== 3) return null;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsPart);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
  return hours * 3600 + minutes * 60 + seconds;
};

const formatDuration = (value) => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'number') {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = Math.floor(value % 60);
    return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
  }
  if (typeof value !== 'string') return '--';
  const parts = value.split(':');
  if (parts.length !== 3) return value;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = hoursRaw.includes('.') ? hoursRaw : hoursRaw.padStart(2, '0');
  const minutes = minutesRaw.padStart(2, '0');
  const seconds = secondsPart.padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const getMetricCandidates = (metric) => {
  if (metric === 'tma') return ['averageAttendanceTime', 'avgAttendanceTime', 'averageFinalizationTime', 'avgFinalizationTime'];
  if (metric === 'first_response') return ['averageFirstResponseTime', 'avgFirstResponseTime'];
  if (metric === 'response') return ['averageResponseTime', 'avgResponseTime'];
  if (metric === 'sla_wait') return ['averageWaitTime', 'avgWaitTime', 'waitTime'];
  return [];
};

const normalizePresenceStatus = (attendantMetrics) => {
  const rawStatus = [attendantMetrics?.currentStatus, attendantMetrics?.status, attendantMetrics?.presence, attendantMetrics?.availability, attendantMetrics?.userData?.status].find((value) => value !== undefined && value !== null && `${value}`.trim() !== '');
  const normalized = String(rawStatus || '').trim().toLowerCase();
  if (['online', 'available', 'active', 'ready', 'on'].includes(normalized)) return 'online';
  if (['away', 'busy', 'idle', 'break', 'pause', 'paused'].includes(normalized)) return 'away';
  if (['offline', 'unavailable', 'disconnected', 'off'].includes(normalized)) return 'offline';
  return 'unknown';
};

const getPresenceLabel = (presence) => (presence === 'online' ? 'Online' : presence === 'away' ? 'Ausente' : presence === 'offline' ? 'Offline' : 'Indisponível');

const isInactiveRankingRow = (row) => {
  const noTma = !Number.isFinite(row.tmaSeconds) || row.tmaSeconds <= 0;
  const noTickets = (row.activeTickets || 0) === 0 && (row.closedTickets || 0) === 0;
  return noTma && noTickets;
};

const buildDateLabel = (periodType, customStart, customEnd) => {
  const now = dayjs();
  if (periodType === 'today') return `Exibindo dados de hoje, ${now.format('DD/MM/YYYY')}`;
  if (periodType === 'week') return 'Exibindo dados desta semana';
  if (periodType === 'month') return `Exibindo dados deste mês, ${now.format('MM/YYYY')}`;
  return `Exibindo de ${dayjs(customStart).format('DD/MM/YYYY')} até ${dayjs(customEnd).format('DD/MM/YYYY')}`;
};

const getPeriodRange = (periodType, customStart, customEnd) => {
  const now = dayjs();
  if (periodType === 'today') return { start: now.startOf('day'), end: now.endOf('day') };
  if (periodType === 'week') return { start: now.startOf('week'), end: now.endOf('week') };
  if (periodType === 'month') return { start: now.startOf('month'), end: now.endOf('month') };
  return { start: dayjs(customStart).startOf('day'), end: dayjs(customEnd).endOf('day') };
};

const getTicketDate = (ticket) => {
  const value = ticket?.createdAt || ticket?.createDate || ticket?.created_date || ticket?.dateTime || ticket?.date;
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const firstNonEmptyString = (...vals) => {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
};

/**
 * Nome exibido no painel — mesma prioridade que OpenTicketCard (Blip: customerName, nome_fantasia, etc.).
 * Evita cair no literal "Cliente" quando só existe label genérico em outro campo.
 */
const getBlipTicketClientLabel = (ticket) =>
  firstNonEmptyString(
    ticket?.customerName,
    ticket?.clientData?.nome_fantasia,
    ticket?.clientData?.name,
    ticket?.clientData?.razao_social,
    ticket?.client?.nome_fantasia,
    ticket?.client?.name,
    ticket?.contact?.name,
    ticket?.contactName
  ) || 'Nome indisponível';

const isTicketAttendanceInProgress = (ticket) => {
  const raw = ticket?.attendanceTime;
  if (raw === '' || raw === null || raw === undefined) return true;
  const fd = formatDuration(raw);
  return fd === '--' || fd === '';
};

const formatOrdinalPt = (n) => {
  if (!Number.isFinite(n) || n < 1) return '—';
  return `${n}\u00BA`;
};

const getMetricFromAttendant = (attendant, metricKey) => {
  const fields = getMetricCandidates(metricKey);
  const raw = fields.map((fieldName) => attendant?.[fieldName] || attendant?.userData?.[fieldName]).find((value) => value !== undefined && value !== null && `${value}`.trim() !== '');
  return { raw, seconds: parseDurationToSeconds(raw) };
};

const getInitials = (name) => String(name || '').trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
const getSlaColor = (percent) => (!Number.isFinite(percent) ? 'rgba(0,0,0,0.2)' : percent < 70 ? '#2E7D32' : percent <= 90 ? '#ED6C02' : '#D32F2F');
const getSlaProgress = (percent) => (!Number.isFinite(percent) ? 0 : Math.max(0, Math.min(100, percent)));

/** Cor do TMA em relação ao SLA de atendimento: verde = folga, amarelo = atenção, vermelho = risco */
const getTmaVsAttendanceSlaStyle = (percent) => {
  if (!Number.isFinite(percent)) {
    return { text: 'rgba(255,255,255,0.5)', bar: 'rgba(255,255,255,0.18)', glow: 'transparent' };
  }
  if (percent < 70) {
    return { text: '#C8E6C9', bar: '#43A047', glow: 'rgba(67,160,71,0.45)' };
  }
  if (percent <= 90) {
    return { text: '#FFE082', bar: '#FB8C00', glow: 'rgba(251,140,0,0.4)' };
  }
  return { text: '#FFAB91', bar: '#E53935', glow: 'rgba(229,57,53,0.5)' };
};
const avatarPalette = ['#1E88E5', '#8E24AA', '#00897B', '#F4511E', '#3949AB', '#6D4C41', '#039BE5', '#7CB342', '#D81B60', '#5E35B1'];
const getAvatarColor = (name) => {
  const key = String(name || '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash << 5) - hash + key.charCodeAt(i);
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};
const getPodiumStyle = (index) => {
  if (index === 0) return { dot: '#F9A825' };
  if (index === 1) return { dot: '#B0BEC5' };
  if (index === 2) return { dot: '#A97142' };
  return null;
};
/** Escala 0–100% para barra + posição vertical da média da equipe (mesma escala). */
const getComparisonBarPercents = (selfValue, teamValue) => {
  const self = Number.isFinite(selfValue) && selfValue >= 0 ? selfValue : 0;
  const team = Number.isFinite(teamValue) && teamValue >= 0 ? teamValue : 0;
  const scale = Math.max(self, team, 1);
  return {
    barPct: Math.min(100, (self / scale) * 100),
    markerPct: Math.min(100, (team / scale) * 100),
  };
};

/**
 * Sparkline com área, escala incluindo opcionalmente a média da equipe para linha de referência.
 * Retorna paths e posição Y da linha tracejada (coordenadas viewBox).
 */
const buildSparklineWithArea = (points, width, chartHeight, teamRefValue) => {
  if (!points.length) {
    return { linePath: '', areaPath: '', refY: null, min: 0, max: 1 };
  }
  const nums = [...points];
  if (Number.isFinite(teamRefValue) && teamRefValue >= 0) nums.push(teamRefValue);
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const spread = max - min || 1;
  const linePath = points.map((value, index) => {
    const x = (index / (points.length - 1 || 1)) * width;
    const y = chartHeight - ((value - min) / spread) * chartHeight;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  const pts = points.map((value, index) => {
    const x = (index / (points.length - 1 || 1)) * width;
    const y = chartHeight - ((value - min) / spread) * chartHeight;
    return { x, y };
  });
  let areaPath = '';
  if (pts.length > 0) {
    let d = `M ${pts[0].x} ${chartHeight} L ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x} ${pts[i].y}`;
    }
    d += ` L ${pts[pts.length - 1].x} ${chartHeight} Z`;
    areaPath = d;
  }
  let refY = null;
  if (Number.isFinite(teamRefValue)) {
    refY = chartHeight - ((teamRefValue - min) / spread) * chartHeight;
  }
  return { linePath, areaPath, refY, min, max };
};

const last7WeekdayLabels = () =>
  Array.from({ length: 7 }, (_, i) =>
    dayjs()
      .subtract(6 - i, 'day')
      .locale('pt-br')
      .format('ddd')
      .replace(/\.$/, '')
      .slice(0, 3)
  );

const pdfStyles = StyleSheet.create({
  page: { padding: 24, fontSize: 10 },
  title: { fontSize: 15, marginBottom: 6, fontWeight: 700 },
  subtitle: { marginBottom: 10 },
  row: { flexDirection: 'row', borderBottom: '1px solid #ddd', paddingVertical: 5 },
  head: { fontWeight: 700, backgroundColor: '#f2f2f2' },
  colPos: { width: '10%' }, colName: { width: '30%' }, colTime: { width: '18%' }, colSla: { width: '14%' }, colActive: { width: '14%' }, colDone: { width: '14%' },
});

const RankingPdf = ({ rows, subtitle }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <Text style={pdfStyles.title}>Ranking de Atendentes</Text>
      <Text style={pdfStyles.subtitle}>{subtitle}</Text>
      <View style={[pdfStyles.row, pdfStyles.head]}>
        <Text style={pdfStyles.colPos}>Pos.</Text><Text style={pdfStyles.colName}>Atendente</Text><Text style={pdfStyles.colTime}>TMA</Text><Text style={pdfStyles.colSla}>SLA espera</Text><Text style={pdfStyles.colActive}>Ativos</Text><Text style={pdfStyles.colDone}>Finalizados</Text>
      </View>
      {rows.map((row, index) => (
        <View key={`${row.id}-${index}`} style={pdfStyles.row}>
          <Text style={pdfStyles.colPos}>#{index + 1}</Text><Text style={pdfStyles.colName}>{row.name}</Text><Text style={pdfStyles.colTime}>{formatDuration(row.tmaRaw)}</Text><Text style={pdfStyles.colSla}>{Number.isFinite(row.waitPercent) ? `${row.waitPercent}%` : '--'}</Text><Text style={pdfStyles.colActive}>{row.activeTickets}</Text><Text style={pdfStyles.colDone}>{row.closedTickets}</Text>
        </View>
      ))}
    </Page>
  </Document>
);

const badgeSx = {
  success: { bgcolor: 'rgba(46,125,50,0.22)', color: '#A5D6A7', border: '1px solid rgba(76,175,80,0.45)' },
  error: { bgcolor: 'rgba(198,40,40,0.2)', color: '#FFCDD2', border: '1px solid rgba(244,67,54,0.45)' },
  warning: { bgcolor: 'rgba(237,108,2,0.18)', color: '#FFE082', border: '1px solid rgba(255,193,7,0.4)' },
  neutral: { bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary', border: '1px solid rgba(255,255,255,0.12)' },
};

const RankingHighlightCard = ({ icon, title, primaryValue, primaryValueColor, name, badgeLabel, badgeTone = 'neutral', accentColor }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      p: 1.75,
      border: '1px solid',
      borderColor: `${accentColor}44`,
      background: `linear-gradient(160deg, ${accentColor}18 0%, rgba(0,0,0,0.2) 45%, rgba(255,255,255,0.02) 100%)`,
      boxShadow: `0 0 0 1px ${accentColor}15, 0 10px 28px rgba(0,0,0,0.35)`,
      minHeight: 148,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 0 0 1px ${accentColor}33, 0 14px 32px rgba(0,0,0,0.4)`,
      },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 800,
          letterSpacing: '0.55px',
          textTransform: 'uppercase',
          color: 'text.secondary',
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          color: '#fff',
          backgroundColor: accentColor,
          borderRadius: '50%',
          width: 26,
          height: 26,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 12px ${accentColor}55`,
          '& .MuiSvgIcon-root': { fontSize: '0.95rem' },
        }}
      >
        {icon}
      </Box>
    </Stack>

    <Typography
      sx={{
        fontSize: { xs: '1.65rem', sm: '1.85rem' },
        fontWeight: 900,
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: primaryValueColor || 'common.white',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {primaryValue ?? '—'}
    </Typography>

    <Typography
      sx={{
        fontSize: '0.8rem',
        fontWeight: 600,
        color: 'text.secondary',
        lineHeight: 1.25,
        opacity: 0.92,
      }}
      noWrap
      title={name}
    >
      {name || '—'}
    </Typography>

    {badgeLabel ? (
      <Box
        component="span"
        sx={{
          alignSelf: 'flex-start',
          mt: 'auto',
          px: 1.1,
          py: 0.35,
          borderRadius: 999,
          fontSize: '0.72rem',
          fontWeight: 800,
          letterSpacing: '0.02em',
          ...badgeSx[badgeTone],
        }}
      >
        {badgeLabel}
      </Box>
    ) : null}
  </Paper>
);

export default function BlipAttendantRankingPage() {
  const theme = useTheme();
  const sparkFillGradientId = useId().replace(/:/g, '');
  const [changed, setChanged] = useState(false);
  const [secondsToRefresh, setSecondsToRefresh] = useState(30);
  const [periodType, setPeriodType] = useState('today');
  const [orderMetric, setOrderMetric] = useState('tma');
  const [onlyAtRisk, setOnlyAtRisk] = useState(false);
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [selectedAttendant, setSelectedAttendant] = useState(null);
  const [customStart, setCustomStart] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [customEnd, setCustomEnd] = useState(dayjs().format('YYYY-MM-DD'));
  const { data: attendantsData } = useFetchData(urlAttendants, {}, changed, true, false, true);
  const { data: openTicketsData } = useFetchData(urlOpenTickets, {}, changed, true, false, true);
  const { data: slaData } = useFetchData(urlSla, {}, changed, true, false, true);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsToRefresh((prev) => {
        if (prev <= 1) {
          setChanged((current) => !current);
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const periodRange = useMemo(() => getPeriodRange(periodType, customStart, customEnd), [periodType, customStart, customEnd]);
  const ticketsByAttendant = useMemo(() => {
    const map = new Map();
    const items = openTicketsData?.items || [];
    items.forEach((ticket) => {
      const attendantId = ticket?.userData?.id;
      if (attendantId === undefined || attendantId === null) return;
      const key = String(attendantId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ticket);
    });
    return map;
  }, [openTicketsData]);

  const rankingRows = useMemo(() => {
    const items = attendantsData?.items || [];
    const waitSla = Number(slaData?.wait_time);
    const attendanceSla = Number(slaData?.attendance_time);
    const rows = items.map((attendant) => {
      const id = attendant?.userData?.id;
      const name = attendant?.userData?.name || attendant?.name || 'Sem nome';
      const status = normalizePresenceStatus(attendant);
      const attendantTickets = id !== undefined && id !== null ? ticketsByAttendant.get(String(id)) || [] : [];
      const periodTickets = attendantTickets.filter((ticket) => {
        const date = getTicketDate(ticket);
        if (!date) return true;
        return date.isAfter(periodRange.start) && date.isBefore(periodRange.end);
      });
      const tmaMetric = getMetricFromAttendant(attendant, 'tma');
      const firstResponseMetric = getMetricFromAttendant(attendant, 'first_response');
      const responseMetric = getMetricFromAttendant(attendant, 'response');
      const waitMetric = getMetricFromAttendant(attendant, 'sla_wait');
      const waitPercent = Number.isFinite(waitMetric.seconds) && Number.isFinite(waitSla) && waitSla > 0 ? Math.round((waitMetric.seconds / waitSla) * 100) : null;
      const tmaSlaPercent =
        Number.isFinite(tmaMetric.seconds) && tmaMetric.seconds > 0 && Number.isFinite(attendanceSla) && attendanceSla > 0
          ? Math.round((tmaMetric.seconds / attendanceSla) * 100)
          : null;
      const atRisk = Number.isFinite(waitPercent) && waitPercent > 100;
      const normalizedTmaOrder =
        Number.isFinite(tmaMetric.seconds) && tmaMetric.seconds > 0 ? tmaMetric.seconds : Number.MAX_SAFE_INTEGER;
      const normalizedFirstResponseOrder =
        Number.isFinite(firstResponseMetric.seconds) && firstResponseMetric.seconds > 0
          ? firstResponseMetric.seconds
          : Number.MAX_SAFE_INTEGER;
      const normalizedResponseOrder =
        Number.isFinite(responseMetric.seconds) && responseMetric.seconds > 0
          ? responseMetric.seconds
          : Number.MAX_SAFE_INTEGER;
      const orderValue = orderMetric === 'finalized' ? Number(attendant?.closedTickets) || 0 : orderMetric === 'active' ? periodTickets.length : orderMetric === 'first_response' ? normalizedFirstResponseOrder : orderMetric === 'response' ? normalizedResponseOrder : orderMetric === 'sla_wait' ? waitPercent ?? Number.MAX_SAFE_INTEGER : normalizedTmaOrder;
      return {
        id: id ?? name,
        name,
        status,
        tmaRaw: tmaMetric.raw,
        tmaSeconds: tmaMetric.seconds,
        tmaSlaPercent,
        firstResponseRaw: firstResponseMetric.raw,
        firstResponseSeconds: firstResponseMetric.seconds,
        responseRaw: responseMetric.raw,
        responseSeconds: responseMetric.seconds,
        waitPercent,
        waitRaw: waitMetric.raw,
        activeTickets: periodTickets.length,
        closedTickets: Number(attendant?.closedTickets) || 0,
        atRisk,
        orderValue,
        tickets: periodTickets,
      };
    });
    const filteredRows = rows.filter((row) => {
      const normalizedName = String(row.name || '').trim().toLowerCase();
      const isBlipSystemUser = normalizedName === 'sirrus blip';
      if (isBlipSystemUser) return false;
      if (onlyAtRisk && !row.atRisk) return false;
      if (onlyOnline && row.status !== 'online') return false;
      return true;
    });
    const sortAsc = !['finalized', 'active'].includes(orderMetric);
    filteredRows.sort((a, b) => (sortAsc ? a.orderValue - b.orderValue : b.orderValue - a.orderValue));
    return filteredRows;
  }, [attendantsData, ticketsByAttendant, periodRange, slaData, onlyAtRisk, onlyOnline, orderMetric]);

  const { rankingRowsActive, rankingRowsInactive } = useMemo(() => {
    const active = [];
    const inactive = [];
    rankingRows.forEach((row) => {
      if (isInactiveRankingRow(row)) inactive.push(row);
      else active.push(row);
    });
    return { rankingRowsActive: active, rankingRowsInactive: inactive };
  }, [rankingRows]);

  const hasFirstResponseData = useMemo(
    () => rankingRows.some((r) => Number.isFinite(r.firstResponseSeconds) && r.firstResponseSeconds > 0),
    [rankingRows]
  );

  const tableQueue = useMemo(() => {
    const out = [];
    rankingRowsActive.forEach((row, i) => out.push({ kind: 'row', row, globalIndex: i, inactiveSection: false }));
    if (rankingRowsActive.length > 0 && rankingRowsInactive.length > 0) {
      out.push({ kind: 'sep' });
    }
    rankingRowsInactive.forEach((row, i) =>
      out.push({ kind: 'row', row, globalIndex: rankingRowsActive.length + i, inactiveSection: true })
    );
    return out;
  }, [rankingRowsActive, rankingRowsInactive]);

  const validTmaRows = rankingRows.filter((row) => Number.isFinite(row.tmaSeconds) && row.tmaSeconds > 0);
  const bestTma = validTmaRows.length ? [...validTmaRows].sort((a, b) => a.tmaSeconds - b.tmaSeconds)[0] : null;
  const worstTma = validTmaRows.length ? [...validTmaRows].sort((a, b) => b.tmaSeconds - a.tmaSeconds)[0] : null;
  const mostFinalized = rankingRows.length ? [...rankingRows].sort((a, b) => b.closedTickets - a.closedTickets)[0] : null;
  const criticalSlaRows = rankingRows.filter((row) => Number.isFinite(row.waitPercent) && row.waitPercent > 0);
  const criticalSla = criticalSlaRows.length
    ? [...criticalSlaRows].sort((a, b) => b.waitPercent - a.waitPercent)[0]
    : null;
  const dateLabel = buildDateLabel(periodType, customStart, customEnd);

  const handleRefresh = () => { setChanged((current) => !current); setSecondsToRefresh(30); };
  const handleExportExcel = () => {
    const fourthLabel = hasFirstResponseData ? '1ª Resposta' : 'Espera cliente';
    const headers = ['Posição', 'Atendente', 'Status', 'TMA', fourthLabel, 'Resposta', 'SLA Espera', 'Ativos', 'Finalizados'];
    const rows = rankingRows.map((row, index) => {
      const fourthCell = hasFirstResponseData ? formatDuration(row.firstResponseRaw) : formatDuration(row.waitRaw);
      return [`#${index + 1}`, row.name, getPresenceLabel(row.status), formatDuration(row.tmaRaw), fourthCell, formatDuration(row.responseRaw), Number.isFinite(row.waitPercent) ? `${row.waitPercent}%` : '--', row.activeTickets, row.closedTickets];
    });
    const tableHtml = `<table><tr>${headers.map((header) => `<th>${header}</th>`).join('')}</tr>${rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('')}</table>`;
    const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ranking-atendentes-${dayjs().format('YYYYMMDD-HHmm')}.xls`; a.click(); URL.revokeObjectURL(url);
  };
  const handleExportPdf = async () => {
    const blob = await pdf(<RankingPdf rows={rankingRows} subtitle={dateLabel} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ranking-atendentes-${dayjs().format('YYYYMMDD-HHmm')}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  const selectedHistory = useMemo(() => {
    if (!selectedAttendant) return [];
    const base = selectedAttendant.tmaSeconds || 0;
    return Array.from({ length: 7 }).map((_, index) => Math.max(0, Math.round(base * (0.9 + index * 0.02))));
  }, [selectedAttendant]);
  const rankingDatePickerSlotProps = useMemo(
    () => ({
      textField: {
        size: 'small',
        sx: { width: { xs: '100%', sm: 168 } },
      },
      desktopPaper: {
        sx: {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.palette.mode === 'dark' ? '0 12px 40px rgba(0,0,0,0.55)' : '0 12px 40px rgba(0,0,0,0.12)',
          bgcolor: theme.palette.mode === 'dark' ? '#1a1d24' : theme.palette.background.paper,
          backgroundImage: 'none',
          '& .MuiPickersCalendarHeader-label': { fontWeight: 700 },
          '& .MuiDayCalendar-weekContainer .MuiPickersDay-root': { fontWeight: 600 },
          '& .MuiPickersDay-root.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 800,
            '&:hover': { bgcolor: 'primary.dark' },
          },
          '& .MuiPickersDay-root:not(.Mui-selected)': {
            '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'action.hover' },
          },
        },
      },
      actionBar: {
        actions: ['clear', 'today'],
      },
    }),
    [theme.palette.mode]
  );

  const teamAverage = useMemo(() => {
    if (!rankingRows.length) return null;
    const avg = (selector) => {
      const valid = rankingRows.map(selector).filter((v) => Number.isFinite(v));
      return valid.length ? valid.reduce((sum, value) => sum + value, 0) / valid.length : null;
    };
    return {
      tma: avg((row) => row.tmaSeconds),
      firstResponse: avg((row) => row.firstResponseSeconds),
      response: avg((row) => row.responseSeconds),
      waitPercent: avg((row) => row.waitPercent),
      closedTickets: avg((row) => row.closedTickets),
    };
  }, [rankingRows]);

  const sparklineWeekLabels = useMemo(() => last7WeekdayLabels(), []);
  const sparklineChart = useMemo(() => {
    const chartW = 280;
    const chartH = 78;
    const teamRef = teamAverage?.tma;
    return buildSparklineWithArea(selectedHistory, chartW, chartH, Number.isFinite(teamRef) ? teamRef : null);
  }, [selectedHistory, teamAverage?.tma]);

  /** Resumo no topo do drawer: métricas do atendente selecionado vs equipe e posições (alinhado ao período do filtro da tela). */
  const drawerHeadlineStats = useMemo(() => {
    if (!selectedAttendant || !rankingRows.length) return null;
    const tableIdx = rankingRows.findIndex((r) => r.id === selectedAttendant.id);
    const rankInTable = tableIdx >= 0 ? tableIdx + 1 : null;
    const byFinal = [...rankingRows].sort((a, b) => b.closedTickets - a.closedTickets);
    const finIdx = byFinal.findIndex((r) => r.id === selectedAttendant.id);
    const rankByFinalized = finIdx >= 0 ? finIdx + 1 : null;
    let tmaPctVsTeam = null;
    if (
      Number.isFinite(teamAverage?.tma) &&
      teamAverage.tma > 0 &&
      Number.isFinite(selectedAttendant.tmaSeconds) &&
      selectedAttendant.tmaSeconds > 0
    ) {
      tmaPctVsTeam = Math.round(((selectedAttendant.tmaSeconds - teamAverage.tma) / teamAverage.tma) * 100);
    }
    const tmaLabel =
      periodType === 'today'
        ? 'TMA hoje'
        : periodType === 'week'
          ? 'TMA na semana'
          : periodType === 'month'
            ? 'TMA no mês'
            : 'TMA no período';
    const finalizedScope =
      periodType === 'today'
        ? 'hoje'
        : periodType === 'week'
          ? 'na semana'
          : periodType === 'month'
            ? 'no mês'
            : 'no período';
    return {
      rankInTable,
      rankByFinalized,
      totalAttendants: rankingRows.length,
      tmaPctVsTeam,
      tmaLabel,
      finalizedScope,
    };
  }, [selectedAttendant, rankingRows, teamAverage, periodType]);

  const highlightInsights = useMemo(() => {
    const avgTma = teamAverage?.tma;
    const avgWait = teamAverage?.waitPercent;
    const avgClosed =
      rankingRows.length > 0 ? rankingRows.reduce((sum, row) => sum + row.closedTickets, 0) / rankingRows.length : null;

    const pctTmaVsTeam = (seconds) => {
      if (!Number.isFinite(avgTma) || avgTma <= 0 || !Number.isFinite(seconds) || seconds <= 0) return null;
      return Math.round(((seconds - avgTma) / avgTma) * 100);
    };

    const bestPct = bestTma ? pctTmaVsTeam(bestTma.tmaSeconds) : null;
    const worstPct = worstTma ? pctTmaVsTeam(worstTma.tmaSeconds) : null;

    let finalizedBadge = 'atendimentos';
    let finalizedTone = 'neutral';
    if (mostFinalized && avgClosed != null && avgClosed > 0 && mostFinalized.closedTickets >= 0) {
      const p = Math.round(((mostFinalized.closedTickets - avgClosed) / avgClosed) * 100);
      finalizedBadge = `${p >= 0 ? '+' : ''}${p}% da média`;
      finalizedTone = p > 0 ? 'success' : p < 0 ? 'warning' : 'neutral';
    }

    let slaPrimary = '—';
    let slaName = 'Nenhum em risco';
    let slaBadge = 'Tudo ok';
    let slaTone = 'success';
    let slaValueColor = '#B0BEC5';
    if (criticalSla && Number.isFinite(criticalSla.waitPercent)) {
      slaPrimary = `${criticalSla.waitPercent}%`;
      slaName = criticalSla.name;
      slaValueColor = '#FF8A80';
      if (Number.isFinite(avgWait) && avgWait > 0) {
        const p = Math.round(((criticalSla.waitPercent - avgWait) / avgWait) * 100);
        slaBadge = `${p >= 0 ? '+' : ''}${p}% vs média equipe`;
        slaTone = 'error';
      } else {
        slaBadge = 'SLA de espera';
        slaTone = 'error';
      }
    }

    return {
      bestBadge: bestPct != null ? `${bestPct >= 0 ? '+' : ''}${bestPct}% da média` : 'melhor tempo',
      bestTone: bestPct != null && bestPct <= 0 ? 'success' : bestPct != null && bestPct > 0 ? 'warning' : 'neutral',
      worstBadge: worstPct != null ? `${worstPct >= 0 ? '+' : ''}${worstPct}% da média` : '—',
      worstTone: worstPct != null && worstPct > 0 ? 'error' : worstPct != null && worstPct <= 0 ? 'success' : 'neutral',
      finalizedBadge,
      finalizedTone,
      slaPrimary,
      slaName,
      slaBadge,
      slaTone,
      slaValueColor,
    };
  }, [teamAverage, rankingRows, bestTma, worstTma, mostFinalized, criticalSla]);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
          <Box>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800 }}>Ranking de Atendentes</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', fontWeight: 600 }}>{dateLabel}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={handleRefresh}>Atualizar</Button>
            <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={handleExportExcel}>Excel</Button>
            <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={handleExportPdf}>PDF</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} useFlexGap flexWrap="wrap">
          <Stack direction="row" spacing={1}>
            {[{ id: 'today', label: 'Hoje' }, { id: 'week', label: 'Esta semana' }, { id: 'month', label: 'Este mês' }, { id: 'custom', label: 'Personalizado' }].map((option) => (
              <Button key={option.id} size="small" variant={periodType === option.id ? 'contained' : 'outlined'} onClick={() => setPeriodType(option.id)} sx={{ textTransform: 'none' }}>{option.label}</Button>
            ))}
          </Stack>
          {periodType === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <DatePicker
                  label="Início"
                  format="DD/MM/YYYY"
                  value={dayjs(customStart)}
                  onChange={(value) => {
                    if (value && value.isValid()) setCustomStart(value.format('YYYY-MM-DD'));
                  }}
                  slotProps={rankingDatePickerSlotProps}
                />
                <DatePicker
                  label="Fim"
                  format="DD/MM/YYYY"
                  value={dayjs(customEnd)}
                  minDate={dayjs(customStart)}
                  onChange={(value) => {
                    if (value && value.isValid()) setCustomEnd(value.format('YYYY-MM-DD'));
                  }}
                  slotProps={rankingDatePickerSlotProps}
                />
              </Stack>
            </LocalizationProvider>
          )}
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel id="ranking-order-label">Ordenar por</InputLabel>
            <Select labelId="ranking-order-label" label="Ordenar por" value={orderMetric} onChange={(event) => setOrderMetric(event.target.value)}>
              <MenuItem value="tma">Tempo Médio de Atendimento (TMA)</MenuItem>
              <MenuItem value="first_response">Tempo Médio de 1ª Resposta</MenuItem>
              <MenuItem value="response">Tempo Médio de Resposta</MenuItem>
              <MenuItem value="sla_wait">SLA de Espera</MenuItem>
              <MenuItem value="finalized">Atendimentos Finalizados</MenuItem>
              <MenuItem value="active">Atendimentos Ativos</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <Stack direction="row" spacing={0.5} alignItems="center"><Switch checked={onlyAtRisk} onChange={(event) => setOnlyAtRisk(event.target.checked)} /><Typography sx={{ fontSize: '0.85rem' }}>Apenas em risco de SLA</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Switch checked={onlyOnline} onChange={(event) => setOnlyOnline(event.target.checked)} /><Typography sx={{ fontSize: '0.85rem' }}>Apenas online agora</Typography></Stack>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' } }}>
        <RankingHighlightCard
          icon={<EmojiEventsOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
          title={periodType === 'month' ? 'Melhor TMA do mês' : 'Melhor TMA hoje'}
          primaryValue={bestTma ? formatDuration(bestTma.tmaRaw) : '—'}
          primaryValueColor="#C8E6C9"
          name={bestTma?.name || 'Sem dados'}
          badgeLabel={highlightInsights.bestBadge}
          badgeTone={highlightInsights.bestTone}
          accentColor="#2E7D32"
        />
        <RankingHighlightCard
          icon={<TrendingDownOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
          title="Maior TMA"
          primaryValue={worstTma ? formatDuration(worstTma.tmaRaw) : '—'}
          primaryValueColor="#FFCDD2"
          name={worstTma?.name || 'Sem dados'}
          badgeLabel={highlightInsights.worstBadge}
          badgeTone={highlightInsights.worstTone}
          accentColor="#C62828"
        />
        <RankingHighlightCard
          icon={<GroupsOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
          title="Mais finalizados"
          primaryValue={mostFinalized != null ? String(mostFinalized.closedTickets) : '—'}
          primaryValueColor="#BBDEFB"
          name={mostFinalized?.name || '—'}
          badgeLabel={highlightInsights.finalizedBadge}
          badgeTone={highlightInsights.finalizedTone}
          accentColor="#1565C0"
        />
        <RankingHighlightCard
          icon={<WarningAmberOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
          title="SLA mais crítico"
          primaryValue={highlightInsights.slaPrimary}
          primaryValueColor={highlightInsights.slaValueColor}
          name={highlightInsights.slaName}
          badgeLabel={highlightInsights.slaBadge}
          badgeTone={highlightInsights.slaTone}
          accentColor="#C62828"
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                background: 'linear-gradient(90deg, rgba(25,118,210,0.28) 0%, rgba(67,160,71,0.16) 100%)',
              }}
            >
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>Posição</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>Atendente</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>TMA</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>
                {hasFirstResponseData ? '1ª resposta' : 'Espera cliente'}
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>Resposta</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>SLA espera</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>Ativos</TableCell>
              <TableCell sx={{ fontWeight: 900, color: 'common.white', letterSpacing: '0.2px' }}>Finalizados</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!rankingRows.length && <TableRow><TableCell colSpan={8} align="center">Nenhum dado disponível para os filtros selecionados.</TableCell></TableRow>}
            {rankingRows.length > 0 &&
              tableQueue.map((item) => {
                if (item.kind === 'sep') {
                  return (
                    <TableRow key="__sep_inactive__" sx={{ pointerEvents: 'none' }}>
                      <TableCell
                        colSpan={8}
                        sx={{
                          py: 0.85,
                          px: 2,
                          backgroundColor: 'rgba(0,0,0,0.22)',
                          borderTop: '1px dashed rgba(255,255,255,0.12)',
                          borderBottom: '1px dashed rgba(255,255,255,0.08)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 0.5,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            fontSize: '0.68rem',
                          }}
                        >
                          Sem atividade hoje
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                const { row, globalIndex, inactiveSection } = item;
                const podium = getPodiumStyle(globalIndex);
                const hasActivity = (row.activeTickets || 0) > 0 || (row.closedTickets || 0) > 0;
                const isOnline = row.status === 'online';
                const rowOpacity = inactiveSection ? 0.42 : isOnline || hasActivity ? 1 : 0.62;
                const activeColor = row.activeTickets > 3 ? '#FFD54F' : row.activeTickets > 0 ? '#66BB6A' : 'rgba(255,255,255,0.45)';
                const closedColor = row.closedTickets > 0 ? '#81C784' : 'rgba(255,255,255,0.45)';
                const avatarColor = getAvatarColor(row.name);
                const tmaRiskStyle = getTmaVsAttendanceSlaStyle(row.tmaSlaPercent);
                const zebraBg = inactiveSection
                  ? globalIndex % 2 === 0
                    ? 'rgba(255,255,255,0.015)'
                    : 'rgba(255,255,255,0.03)'
                  : globalIndex % 2 === 0
                    ? 'rgba(255,255,255,0.01)'
                    : 'rgba(255,255,255,0.03)';
                const leaderBg = !inactiveSection && globalIndex === 0 ? 'rgba(249,168,37,0.08)' : zebraBg;
                const fourthCell = hasFirstResponseData ? formatDuration(row.firstResponseRaw) : formatDuration(row.waitRaw);
                return (
              <TableRow
                key={row.id}
                hover
                onClick={() => setSelectedAttendant(row)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: leaderBg,
                  opacity: rowOpacity,
                  '&:hover': {
                    backgroundColor: 'rgba(25,118,210,0.16)',
                    opacity: 1,
                  },
                }}
              >
                <TableCell>
                  {podium ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }} title={`#${globalIndex + 1}`}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: podium.dot,
                        }}
                      />
                      <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: 'rgba(255,255,255,0.92)' }}>
                        #{globalIndex + 1}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip size="small" label={`#${globalIndex + 1}`} variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.66rem',
                        backgroundColor: avatarColor,
                        color: '#fff',
                        fontWeight: 700,
                        opacity: isOnline ? 1 : hasActivity ? 0.85 : 0.45,
                      }}
                    >
                      {getInitials(row.name)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: 'common.white', opacity: isOnline ? 1 : 0.9 }}>
                        {row.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: row.status === 'online' ? '#66BB6A' : row.status === 'away' ? '#FFB74D' : 'text.secondary', fontWeight: 700 }}>
                        {getPresenceLabel(row.status)}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'middle', py: 1 }}>
                  <Stack spacing={0.35}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.88rem',
                        fontVariantNumeric: 'tabular-nums',
                        color: tmaRiskStyle.text,
                      }}
                    >
                      {formatDuration(row.tmaRaw)}
                    </Typography>
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 56, height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${getSlaProgress(row.tmaSlaPercent)}%`,
                            height: '100%',
                            borderRadius: 999,
                            background: tmaRiskStyle.bar,
                            boxShadow: `0 0 6px ${tmaRiskStyle.glow}`,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          fontVariantNumeric: 'tabular-nums',
                          color: tmaRiskStyle.text,
                          minWidth: 34,
                          textAlign: 'right',
                        }}
                      >
                        {Number.isFinite(row.tmaSlaPercent) ? `${row.tmaSlaPercent}%` : '—'}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'common.white' }}>{fourthCell}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'common.white' }}>{formatDuration(row.responseRaw)}</TableCell>
                <TableCell>
                  <Stack spacing={0.45}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>
                      {Number.isFinite(row.waitPercent) ? `${row.waitPercent}%` : '--'}
                    </Typography>
                    <Box sx={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${getSlaProgress(row.waitPercent)}%`,
                          height: '100%',
                          background: getSlaColor(row.waitPercent),
                          boxShadow: `0 0 8px ${getSlaColor(row.waitPercent)}`,
                        }}
                      />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: activeColor }}>{row.activeTickets}</TableCell>
                <TableCell sx={{ fontWeight: 900, color: closedColor }}>{row.closedTickets}</TableCell>
              </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', textAlign: 'right' }}>Atualiza em {secondsToRefresh}s</Typography>

      <Drawer anchor="right" open={Boolean(selectedAttendant)} onClose={() => setSelectedAttendant(null)}>
        <Box sx={{ width: { xs: 340, sm: 420 }, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>{selectedAttendant?.name}</Typography>
          {drawerHeadlineStats && selectedAttendant ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 1,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.15,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(255,255,255,0.03)',
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.45, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5, lineHeight: 1.2 }}>
                  {drawerHeadlineStats.tmaLabel}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, color: 'common.white' }}>
                  {formatDuration(selectedAttendant.tmaRaw)}
                </Typography>
                {drawerHeadlineStats.tmaPctVsTeam != null ? (
                  <Box
                    sx={{
                      mt: 0.65,
                      display: 'inline-flex',
                      maxWidth: '100%',
                      px: 0.65,
                      py: 0.2,
                      borderRadius: 999,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      lineHeight: 1.2,
                      bgcolor:
                        drawerHeadlineStats.tmaPctVsTeam > 0
                          ? 'rgba(211,47,47,0.2)'
                          : drawerHeadlineStats.tmaPctVsTeam < 0
                            ? 'rgba(46,125,50,0.2)'
                            : 'rgba(255,255,255,0.06)',
                      color:
                        drawerHeadlineStats.tmaPctVsTeam > 0
                          ? '#FFCDD2'
                          : drawerHeadlineStats.tmaPctVsTeam < 0
                            ? '#C8E6C9'
                            : 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {`${drawerHeadlineStats.tmaPctVsTeam >= 0 ? '+' : ''}${drawerHeadlineStats.tmaPctVsTeam}% da média`}
                  </Box>
                ) : (
                  <Typography sx={{ mt: 0.45, fontSize: '0.6rem', color: 'text.secondary' }}>Sem comparação</Typography>
                )}
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: 1.15,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(255,255,255,0.03)',
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.45, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5, lineHeight: 1.2 }}>
                  Finalizados
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', mb: 0.35, lineHeight: 1.2 }}>({drawerHeadlineStats.finalizedScope})</Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, color: 'common.white' }}>
                  {selectedAttendant.closedTickets}
                </Typography>
                {drawerHeadlineStats.rankByFinalized != null ? (
                  <Box
                    sx={{
                      mt: 0.65,
                      display: 'inline-flex',
                      maxWidth: '100%',
                      px: 0.65,
                      py: 0.2,
                      borderRadius: 999,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      lineHeight: 1.2,
                      bgcolor: 'rgba(46,125,50,0.18)',
                      color: '#C8E6C9',
                      border: '1px solid rgba(76,175,80,0.35)',
                    }}
                  >
                    {drawerHeadlineStats.rankByFinalized === 1
                      ? 'Mais ativo'
                      : `${formatOrdinalPt(drawerHeadlineStats.rankByFinalized)} mais ativo`}
                  </Box>
                ) : (
                  <Typography sx={{ mt: 0.45, fontSize: '0.6rem', color: 'text.secondary' }}>—</Typography>
                )}
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: 1.15,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(255,255,255,0.03)',
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.45, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5, lineHeight: 1.2 }}>
                  Posição
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, color: 'common.white' }}>
                  {drawerHeadlineStats.rankInTable != null ? `#${formatOrdinalPt(drawerHeadlineStats.rankInTable)}` : '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600, mt: 0.35, lineHeight: 1.25 }}>
                  de {drawerHeadlineStats.totalAttendants} atendentes
                </Typography>
              </Paper>
            </Box>
          ) : null}
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, mb: 0.5 }}>TMA — últimos 7 dias</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.68rem' }}>
              Linha tracejada = média da equipe (TMA)
            </Typography>
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <svg width="100%" viewBox="0 0 280 112" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                <defs>
                  <linearGradient id={sparkFillGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#42A5F5" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#1976d2" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {sparklineChart.areaPath ? <path d={sparklineChart.areaPath} fill={`url(#${sparkFillGradientId})`} /> : null}
                {Number.isFinite(sparklineChart.refY) ? (
                  <line
                    x1="0"
                    y1={sparklineChart.refY}
                    x2="280"
                    y2={sparklineChart.refY}
                    stroke="rgba(255,255,255,0.5)"
                    strokeWidth="1"
                    strokeDasharray="5 5"
                  />
                ) : null}
                {sparklineChart.linePath ? (
                  <path d={sparklineChart.linePath} fill="none" stroke="#64B5F6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                ) : null}
                {sparklineWeekLabels.map((label, i) => (
                  <text
                    key={`spark-dow-${i}`}
                    x={(i / Math.max(1, sparklineWeekLabels.length - 1)) * 280}
                    y="108"
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.55)"
                    style={{ fontSize: '9px' }}
                  >
                    {label}
                  </text>
                ))}
              </svg>
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, mb: 1 }}>Últimos atendimentos</Typography>
            <Stack spacing={1}>
              {(selectedAttendant?.tickets || []).slice(0, 6).map((ticket, index) => {
                const open = isTicketAttendanceInProgress(ticket);
                return (
                  <Box key={`${ticket?.sequentialId || 't'}-${index}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.8 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                      Ticket #{ticket?.sequentialId || '--'} — {getBlipTicketClientLabel(ticket)}
                    </Typography>
                    {open ? (
                      <Stack direction="row" spacing={0.85} alignItems="center" sx={{ mt: 0.25 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: '#EF5350',
                            boxShadow: '0 0 6px rgba(239,83,80,0.45)',
                          }}
                        />
                        <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary', fontWeight: 600 }}>Em andamento</Typography>
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary', mt: 0.25 }}>
                        Atendimento: {formatDuration(ticket?.attendanceTime)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              {!(selectedAttendant?.tickets || []).length && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Sem lista de tickets para o período selecionado.</Typography>
              )}
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, mb: 0.5 }}>Comparação com média da equipe</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5, fontSize: '0.68rem' }}>
              Linha vertical = média da equipe (mesma escala da barra)
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'text.secondary' }}>TMA</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#E53935', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDuration(selectedAttendant?.tmaRaw)}
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative', height: 10, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'visible' }}>
                  {(() => {
                    const { barPct, markerPct } = getComparisonBarPercents(selectedAttendant?.tmaSeconds, teamAverage?.tma);
                    return (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${barPct}%`,
                            borderRadius: 999,
                            bgcolor: '#E53935',
                            boxShadow: '0 0 8px rgba(229,57,53,0.35)',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${markerPct}%`,
                            top: -3,
                            width: 2,
                            height: 16,
                            marginLeft: '-1px',
                            bgcolor: 'rgba(255,255,255,0.9)',
                            borderRadius: 0.5,
                            zIndex: 1,
                            boxShadow: '0 0 4px rgba(0,0,0,0.35)',
                          }}
                          title="Média da equipe"
                        />
                      </>
                    );
                  })()}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.35, display: 'block' }}>
                  Média equipe: {formatDuration(teamAverage?.tma)}
                </Typography>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'text.secondary' }}>Resposta</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#FB8C00', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDuration(selectedAttendant?.responseRaw)}
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative', height: 10, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'visible' }}>
                  {(() => {
                    const { barPct, markerPct } = getComparisonBarPercents(selectedAttendant?.responseSeconds, teamAverage?.response);
                    return (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${barPct}%`,
                            borderRadius: 999,
                            bgcolor: '#FB8C00',
                            boxShadow: '0 0 8px rgba(251,140,0,0.35)',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${markerPct}%`,
                            top: -3,
                            width: 2,
                            height: 16,
                            marginLeft: '-1px',
                            bgcolor: 'rgba(255,255,255,0.9)',
                            borderRadius: 0.5,
                            zIndex: 1,
                            boxShadow: '0 0 4px rgba(0,0,0,0.35)',
                          }}
                          title="Média da equipe"
                        />
                      </>
                    );
                  })()}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.35, display: 'block' }}>
                  Média equipe: {formatDuration(teamAverage?.response)}
                </Typography>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'text.secondary' }}>Finalizados</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#66BB6A', fontVariantNumeric: 'tabular-nums' }}>
                    {selectedAttendant != null ? selectedAttendant.closedTickets : '—'}
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative', height: 10, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'visible' }}>
                  {(() => {
                    const { barPct, markerPct } = getComparisonBarPercents(selectedAttendant?.closedTickets, teamAverage?.closedTickets);
                    return (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${barPct}%`,
                            borderRadius: 999,
                            bgcolor: '#43A047',
                            boxShadow: '0 0 8px rgba(67,160,71,0.35)',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${markerPct}%`,
                            top: -3,
                            width: 2,
                            height: 16,
                            marginLeft: '-1px',
                            bgcolor: 'rgba(255,255,255,0.9)',
                            borderRadius: 0.5,
                            zIndex: 1,
                            boxShadow: '0 0 4px rgba(0,0,0,0.35)',
                          }}
                          title="Média da equipe"
                        />
                      </>
                    );
                  })()}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.35, display: 'block' }}>
                  Média equipe:{' '}
                  {Number.isFinite(teamAverage?.closedTickets) ? teamAverage.closedTickets.toFixed(1).replace(/\.0$/, '') : '—'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Drawer>
    </Box>
  );
}
